import axios from 'axios';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

const AUTH_TOKEN_KEY = 'dental_events_token';

export const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    try {
      localStorage.setItem(AUTH_TOKEN_KEY, token);
    } catch (e) {}
  } else {
    delete api.defaults.headers.common['Authorization'];
    try {
      localStorage.removeItem(AUTH_TOKEN_KEY);
    } catch (e) {}
  }
};

export const getStoredToken = () => {
  try {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  } catch (e) {
    return null;
  }
};

// Auth
export const authCheck = () => api.get('/auth/check');
export const login = (password) => api.post('/auth/login', { password });

// Dashboard & Analytics
export const getDashboard = () => api.get('/analytics/dashboard');
export const getAdStats = (params) => api.get('/analytics/ad-stats', { params });
/** Pull audience/targeting detail for ads — by id, event or clinic. 1 credit per ad. */
export const enrichAds = (body) => api.post('/ads/enrich', body);
/** Enriched ads broken down by targeted city and UK region. */
export const getTargeting = (params) => api.get('/analytics/targeting', { params });
/** The analysis hub: lead time, longevity, creative mix, lead forms, spend… */
export const getReports = (params) => api.get('/analytics/reports', { params });
/** CPM assumptions behind every estimated-spend figure. */
export const getCpmSettings = () => api.get('/settings/cpm');
export const updateCpmSettings = (body) => api.put('/settings/cpm', body || {});
/** Preview / apply clinic-name canonicalisation. */
export const previewClinicNames = () => api.post('/normalize-clinics', { dryRun: true });
export const applyClinicNames = () => api.post('/normalize-clinics', {});
export const getStats = () => api.get('/stats');

// Clinics & Locations intelligence
export const getClinics = (params) => api.get('/clinics', { params });
export const getClinicByName = (name) => api.get(`/clinics/${encodeURIComponent(name)}`);
export const getLocationByName = (name) =>
  api.get(`/locations/profile/${encodeURIComponent(name)}`);
export const getLocationResearch = (name) =>
  api.get(`/locations/${encodeURIComponent(name)}/research`);
export const getMapLocations = () => api.get('/map/locations');

// Events
export const getEvents = (params) => api.get('/events', { params });
export const getEventById = (id) => api.get(`/events/${id}`);
export const getEventsCalendar = (params) => api.get('/events/calendar', { params });
export const getEventConflicts = (params) => api.get('/events/conflicts', { params });
export const getLocations = () => api.get('/events/locations');

// Raw Ads
export const getRawAds = (params) => api.get('/raw-ads', { params });
export const getRawAdById = (id) => api.get(`/raw-ads/${id}`);
export const getAdMetricsHistory = (id) => api.get(`/raw-ads/${id}/metrics-history`);

// Search
export const searchAds = (data) => api.post('/search', data);
export const getSearchHistory = (params) => api.get('/searches', { params });

// Fetch Events — batch mode (Hobby plan: call repeatedly until nextIndex === null)
export const fetchNewEvents = (params = {}) => api.post('/fetch-events', params);

// AI Processing — batch mode (call repeatedly until remaining === 0)
export const triggerAIProcessing = (batchSize = 16) => api.post('/process', { batchSize });
export const triggerProcessing = () => api.post('/process', { batchSize: 16 });

// Background analysis — keeps running server-side even if the tab closes.
export const startBackgroundAnalysis = (batchSize = 16) => api.post('/process/background', { batchSize });
export const stopBackgroundAnalysis = () => api.post('/process/background/stop');
export const getBackgroundStatus = () => api.get('/process/background/status');

// Processing diagnostics (counts by bucket + recent unprocessed samples)
export const getProcessingStats = () => api.get('/raw-ads/processing-stats');

// Maintenance — AI duplicate detection + clinic/location name fixes.
// Step 1: previewCleanup() returns a duplicatePlan without writing anything.
// Step 2: applyCleanup(plan) soft-merges exactly the previewed pairs.
export const previewCleanup = () => api.post('/cleanup-events', { dryRun: true });
export const applyCleanup = (duplicatePlan) =>
  api.post('/cleanup-events', { applyMerges: (duplicatePlan || []).map((m) => ({ dupId: m.dupId, keeperId: m.keeperId })) });
// Back-compat: one-shot detect + apply (no preview).
export const cleanupEvents = (dryRun = false) => api.post('/cleanup-events', { dryRun });

const SESSION_EXPIRED_KEY = 'dental_events_session_expired';

// On 401, clear token, set session-expired flag, and notify app to show login again
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      setAuthToken(null);
      if (typeof window !== 'undefined') {
        try {
          sessionStorage.setItem(SESSION_EXPIRED_KEY, '1');
        } catch (e) {}
        window.dispatchEvent(new CustomEvent('auth:logout'));
      }
    }
    return Promise.reject(err);
  }
);

export function getSessionExpiredFlag() {
  try {
    return sessionStorage.getItem(SESSION_EXPIRED_KEY) === '1';
  } catch (e) {
    return false;
  }
}

export function clearSessionExpiredFlag() {
  try {
    sessionStorage.removeItem(SESSION_EXPIRED_KEY);
  } catch (e) {}
}

export default api;
